import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Check, Info, SlidersHorizontal, Loader2, MapPin, Landmark, Globe, KeyRound } from "lucide-react";
import api, { apiError } from "@/lib/crmApi";
import { ANCIENNETE_TILES, ancienneteSummary } from "@/crm/anciennete";
import Modal from "@/crm/components/Modal";
import { cn } from "@/lib/utils";

const MAX_DAYS = 3650; // borne du backend (CampaignCreate.anciennete_max_jours)

const LABEL = "mb-1.5 block text-xs font-semibold text-[#475569]";
const INPUT = "h-[42px] w-full rounded-[10px] border border-[#d0d0d0] bg-white px-3 text-sm text-[#0f172a] outline-none transition-all focus:border-[#005bd3] focus:ring-[3px] focus:ring-[rgba(0,91,211,.16)]";
const MONO = "font-mono";

/**
 * Création / modification d'une campagne d'extraction.
 *
 * Deux sources, une par campagne : le registre officiel (SIRENE, filtré par
 * NAF) ou Google Places (filtré par type d'établissement indexé par Google).
 * Les critères affichés suivent la source — NAF, état administratif et
 * ancienneté n'ont pas de sens chez Google, qui ne rend ni SIRET ni date.
 *
 * Les critères restent modifiables tant que la campagne n'a jamais tourné (le
 * backend refuse ensuite). Le filtre d'ancienneté se choisit par tuiles ou par
 * une fenêtre libre en jours — une seule valeur derrière les deux.
 */
export default function CampaignForm({ campaign, onClose, onSaved }) {
  const isEdit = !!campaign;
  const [f, setF] = useState(() => ({
    name: campaign?.name || "",
    source: campaign?.source || "recherche-entreprises",
    google_type: campaign?.google_type || "",
    departement: campaign?.departement || "",
    // Stocké en tableau côté API, saisi en texte libre dans le formulaire.
    codes_postaux: (campaign?.codes_postaux || []).join(" "),
    naf: campaign?.naf || "",
    section_activite: campaign?.section_activite || "",
    etat_administratif: campaign?.etat_administratif || "A",
    query: campaign?.query || "",
    objectif: campaign?.objectif ?? 200,
    anciennete_max_jours: campaign?.anciennete_max_jours ? String(campaign.anciennete_max_jours) : "",
  }));
  const [catalog, setCatalog] = useState([]);
  const [domainSlug, setDomainSlug] = useState("");
  const [gCatalog, setGCatalog] = useState([]);
  const [gDomainSlug, setGDomainSlug] = useState("");
  const [sources, setSources] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/settings/naf-catalog").then(({ data }) => setCatalog(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/settings/google-types-catalog").then(({ data }) => setGCatalog(Array.isArray(data) ? data : [])).catch(() => {}); }, []);
  useEffect(() => { api.get("/campaigns/sources").then(({ data }) => setSources(Array.isArray(data) ? data : [])).catch(() => setSources([])); }, []);

  const set = (patch) => setF((prev) => ({ ...prev, ...patch }));
  const isGoogle = f.source === "google_places";
  const googleSource = sources?.find((s) => s.key === "google_places");
  const domain = catalog.find((d) => d.slug === domainSlug);
  const pickDomain = (slug) => {
    setDomainSlug(slug);
    const d = catalog.find((x) => x.slug === slug);
    const primary = d?.codes.find((c) => c.primary) || d?.codes[0];
    if (primary) set({ naf: primary.code });
  };

  // Type Google : même geste que le NAF — un domaine, puis un type ; le type
  // principal du domaine est proposé d'office. En modification, le domaine
  // se retrouve à partir du type enregistré.
  const gDomain = gCatalog.find((d) => d.slug === gDomainSlug)
    || (f.google_type ? gCatalog.find((d) => d.types.some((t) => t.type === f.google_type)) : null);
  const pickGDomain = (slug) => {
    setGDomainSlug(slug);
    const d = gCatalog.find((x) => x.slug === slug);
    const primary = d?.types.find((t) => t.primary) || d?.types[0];
    set({ google_type: primary ? primary.type : "" });
  };
  const cpCount = parseCodes(f.codes_postaux).length;
  const gTypeLabel = gCatalog.flatMap((d) => d.types).find((t) => t.type === f.google_type)?.label || "";

  const days = f.anciennete_max_jours;
  const tileDays = ANCIENNETE_TILES.flatMap((g) => g.options).map((o) => (o.days == null ? "" : String(o.days)));
  const onTile = tileDays.includes(days);
  const onCustom = (raw) => {
    const v = raw.replace(/[^0-9]/g, "").slice(0, 4);
    set({ anciennete_max_jours: v && Number(v) > 0 ? v : "" });
  };

  const save = async () => {
    if (!f.name.trim()) return toast.error("Nom requis");
    if (isGoogle) {
      if (!f.google_type && !f.query.trim()) return toast.error("Choisissez un type d'établissement Google ou un mot-clé");
      if (!f.departement && !f.codes_postaux) return toast.error("Indiquez des codes postaux ou un département");
    } else if (!f.departement && !f.codes_postaux && !f.query) {
      return toast.error("Renseignez au moins un département, code postal ou mot-clé");
    }
    if (days && Number(days) > MAX_DAYS) return toast.error(`Ancienneté maximale : ${MAX_DAYS} jours`);
    const payload = {
      ...f,
      name: f.name.trim(),
      google_type: isGoogle ? f.google_type || null : null,
      objectif: Number(f.objectif),
      codes_postaux: parseCodes(f.codes_postaux),
      // "" = sans limite d'ancienneté ; le backend attend null, pas une chaîne vide.
      anciennete_max_jours: !isGoogle && days ? Number(days) : null,
    };
    setSaving(true);
    try {
      if (isEdit) await api.put(`/campaigns/${campaign.id}`, payload);
      else await api.post("/campaigns", payload);
      toast.success(isEdit ? "Campagne modifiée" : "Campagne créée");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const titre = isEdit ? "Modifier la campagne" : "Nouvelle campagne";

  return (
    <Modal onClose={onClose} align="top" padded={false} testid="new-campaign-modal" label={titre}
      className="flex max-w-[620px] flex-col overflow-hidden rounded-[18px] shadow-[0_32px_64px_rgba(10,12,24,.28)]">
      {/* En-tête figé */}
      <div className="flex shrink-0 items-start justify-between gap-3 border-b border-[#ebebeb] px-[22px] py-5">
        <div>
          <h2 className="m-0 font-heading text-[19px] font-bold text-[#0b1524]">{titre}</h2>
          <p className="mt-1 text-[13px] text-[#64748b]">
            {isEdit
              ? "Modifiable tant que l'extraction n'a pas été lancée : ensuite les critères sont figés pour rester cohérents avec les entreprises importées."
              : "Les critères restent modifiables jusqu'à la première extraction."}
          </p>
        </div>
        <button type="button" onClick={onClose} aria-label="Fermer" data-testid="campaign-form-close"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] border border-[#e3e3e3] bg-white text-[#64748b] transition-colors hover:bg-[#fafafa] hover:text-[#0f172a]">
          <X size={16} />
        </button>
      </div>

      {/* Corps — seul à défiler */}
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-[22px] py-5">
        <div>
          <label className={LABEL} htmlFor="campaign-name">Nom de la campagne <span className="text-[#d72c0d]">*</span></label>
          <input id="campaign-name" data-testid="campaign-name" value={f.name} onChange={(e) => set({ name: e.target.value })}
            placeholder="Restaurants Rhône 2026" className={INPUT} autoFocus />
        </div>

        {/* Source : une par campagne, ses critères suivent. */}
        <div data-testid="campaign-source">
          <span className={LABEL}>Source des entreprises</span>
          <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-2">
            {SOURCE_TILES.map((t) => {
              const active = f.source === t.key;
              const info = sources?.find((x) => x.key === t.key);
              const unconfigured = info && info.configured === false;
              return (
                <button key={t.key} type="button" onClick={() => set({ source: t.key })}
                  data-testid={`campaign-source-${t.key}`} aria-pressed={active}
                  className={cn("relative rounded-[12px] border px-3 py-2.5 text-left transition-all duration-150",
                    active
                      ? "border-[#005bd3] bg-[#e8f1fd] shadow-[0_0_0_3px_rgba(0,91,211,.14)]"
                      : "border-[#e3e3e3] bg-white shadow-[0_1px_2px_rgba(15,23,42,.04)] hover:border-[#d0d0d0]")}>
                  <div className="flex items-center gap-2">
                    <t.icon size={15} className={active ? "text-[#005bd3]" : "text-[#94a3b8]"} />
                    <span className={cn("text-[13px] font-semibold", active ? "text-[#00427a]" : "text-[#0f172a]")}>{t.label}</span>
                    {unconfigured && (
                      <span data-testid={`source-unconfigured-${t.key}`} title="Aucune clé enregistrée dans Paramètres > Intégrations"
                        className="ml-auto flex items-center gap-1 rounded-md bg-[#fdf3e7] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#8a6116]">
                        <KeyRound size={10} /> clé absente
                      </span>
                    )}
                  </div>
                  <div className={cn("mt-1 text-[11.5px] leading-snug", active ? "text-[#3b82c4]" : "text-[#94a3b8]")}>{info?.description || t.hint}</div>
                  {active && (
                    <span className="absolute right-2 top-2 flex h-4 w-4 items-center justify-center rounded-full bg-[#005bd3] text-white">
                      <Check size={11} />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {isGoogle && googleSource?.configured === false && (
          <div className="flex items-start gap-2 rounded-xl bg-[#fdf3e7] px-3 py-[11px] text-xs leading-[1.5] text-[#8a6116]" data-testid="google-key-hint">
            <KeyRound size={14} className="mt-0.5 shrink-0" />
            <span>Aucune clé Google Places n'est enregistrée : la campagne peut être préparée, mais l'extraction refusera
              de démarrer tant que la clé n'est pas renseignée dans <b>Paramètres › Intégrations</b>.</span>
          </div>
        )}

        <div className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-3">
          {isGoogle ? (
            <>
              <div>
                <label className={LABEL} htmlFor="campaign-gdomain">Domaine d'activité</label>
                <select id="campaign-gdomain" data-testid="campaign-gdomain" value={gDomain?.slug || ""} onChange={(e) => pickGDomain(e.target.value)} className={cn(INPUT, "px-2.5")}>
                  <option value="">— Choisir un domaine —</option>
                  {gCatalog.map((d) => <option key={d.slug} value={d.slug}>{d.domain}</option>)}
                </select>
              </div>
              <div>
                <label className={LABEL} htmlFor="campaign-gtype">Type d'établissement Google</label>
                <select id="campaign-gtype" data-testid="campaign-gtype" value={f.google_type} onChange={(e) => set({ google_type: e.target.value })} className={cn(INPUT, "px-2.5")}>
                  <option value="">— Choisir un type —</option>
                  {(gDomain ? [gDomain] : gCatalog).map((d) => (
                    <optgroup key={d.slug} label={d.domain}>
                      {/* `text_only` : pseudo-type sans équivalent chez Google (DJ…), la
                          recherche se fait sur le libellé seul — voir crm/google_types_catalog.py. */}
                      {d.types.map((t) => <option key={t.type} value={t.type}>{t.label}{t.primary ? " ★" : ""}{t.text_only ? " (recherche libre)" : ""}</option>)}
                    </optgroup>
                  ))}
                </select>
              </div>
            </>
          ) : (
          <>
          <div>
            <label className={LABEL} htmlFor="campaign-sector">Secteur d'activité</label>
            <select id="campaign-sector" data-testid="campaign-sector" value={domainSlug} onChange={(e) => pickDomain(e.target.value)} className={cn(INPUT, "px-2.5")}>
              <option value="">— Choisir un secteur —</option>
              {catalog.map((d) => <option key={d.slug} value={d.slug}>{d.domain}</option>)}
            </select>
          </div>
          {domain ? (
            <div>
              <label className={LABEL} htmlFor="campaign-naf-code">Code NAF du secteur</label>
              <select id="campaign-naf-code" data-testid="campaign-naf-code" value={f.naf} onChange={(e) => set({ naf: e.target.value })} className={cn(INPUT, MONO, "px-2.5")}>
                {domain.codes.map((c) => <option key={c.code} value={c.code}>{c.code} — {c.label}{c.primary ? " ★" : ""}</option>)}
              </select>
            </div>
          ) : (
            <div>
              <label className={LABEL} htmlFor="campaign-naf">Code NAF</label>
              <input id="campaign-naf" data-testid="campaign-naf" value={f.naf} onChange={(e) => set({ naf: e.target.value })}
                placeholder="56.10A" className={cn(INPUT, MONO)} />
            </div>
          )}
          </>
          )}
          <div>
            <label className={LABEL} htmlFor="campaign-dept">Département</label>
            <input id="campaign-dept" data-testid="campaign-dept" value={f.departement} onChange={(e) => set({ departement: e.target.value })}
              placeholder="69" className={cn(INPUT, MONO)} />
          </div>
          <div>
            <label className={LABEL} htmlFor="campaign-obj">Objectif (prospects)</label>
            <input id="campaign-obj" data-testid="campaign-obj" type="number" min="1" value={f.objectif} onChange={(e) => set({ objectif: e.target.value })}
              className={cn(INPUT, MONO)} />
          </div>
        </div>

        {!isGoogle && domain && (
          <div>
            <label className={LABEL} htmlFor="campaign-naf">Code NAF (manuel)</label>
            <input id="campaign-naf" data-testid="campaign-naf" value={f.naf} onChange={(e) => set({ naf: e.target.value })}
              placeholder="56.10A" className={cn(INPUT, MONO)} />
          </div>
        )}

        <div>
          <label className={LABEL} htmlFor="campaign-cp">
            Codes postaux <span className="font-normal text-[#94a3b8]">— séparés par des espaces</span>
          </label>
          <input id="campaign-cp" data-testid="campaign-cp" value={f.codes_postaux} onChange={(e) => set({ codes_postaux: e.target.value })}
            placeholder="69001 69002 69003" className={cn(INPUT, MONO)} />
          <PostalCodePicker departement={f.departement} excludeId={campaign?.id}
            selected={parseCodes(f.codes_postaux)} onChange={(codes) => set({ codes_postaux: codes.join(" ") })} />
        </div>

        <div className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-3">
          <div>
            <label className={LABEL} htmlFor="campaign-query">Mot-clé <span className="font-normal text-[#94a3b8]">— optionnel{isGoogle ? ", ajouté à la recherche Google" : ""}</span></label>
            <input id="campaign-query" data-testid="campaign-query" value={f.query} onChange={(e) => set({ query: e.target.value })}
              placeholder={isGoogle ? "bio, halal, sans gluten…" : "boulangerie"} className={INPUT} />
          </div>
          {!isGoogle && (
            <div>
              <label className={LABEL} htmlFor="campaign-etat">État administratif</label>
              <select id="campaign-etat" data-testid="campaign-etat" value={f.etat_administratif} onChange={(e) => set({ etat_administratif: e.target.value })} className={cn(INPUT, "px-2.5")}>
                <option value="A">Actives</option><option value="C">Cessées</option>
              </select>
            </div>
          )}
        </div>

        {isGoogle && (
          /* Chaque page Text Search est facturée : le coût maximal est dit
             avant de lancer, avec ce qui le fait varier. */
          <div className="flex items-start gap-2 rounded-xl bg-[#e8f1fd] px-3 py-[11px] text-xs leading-[1.5] text-[#00427a]" data-testid="google-cost-hint">
            <Info size={14} className="mt-0.5 shrink-0" />
            <span>{googleCostText(cpCount, f.departement, Number(f.objectif), googleSource, gTypeLabel, f.query)}</span>
          </div>
        )}

        {/* Ancienneté de l'entreprise — registre seulement : Google ne date rien. */}
        {!isGoogle && (
        <div className="rounded-[14px] border border-[#ebebeb] bg-[#fcfcfd] p-3.5" data-testid="campaign-anciennete">
          <div className="mb-1 flex items-center justify-between gap-2.5">
            <span className="text-xs font-semibold text-[#475569]">Ancienneté de l'entreprise</span>
            <button type="button" onClick={() => set({ anciennete_max_jours: "" })} data-testid="campaign-anciennete-reset"
              className="text-xs font-medium text-[#005bd3] transition-colors hover:text-[#00479f]">Réinitialiser</button>
          </div>
          <p className="mb-3 mt-0 text-xs text-[#94a3b8]" data-testid="campaign-anciennete-summary">{ancienneteSummary(days)}</p>
          <div className="flex flex-col gap-3">
            {ANCIENNETE_TILES.map((g) => (
              <div key={g.title}>
                <div className="mb-[7px] text-[11px] font-bold uppercase tracking-[.07em] text-[#94a3b8]">{g.title}</div>
                <div className="grid grid-cols-[repeat(auto-fill,minmax(112px,1fr))] gap-2">
                  {g.options.map((o) => {
                    const value = o.days == null ? "" : String(o.days);
                    const active = days === value;
                    return (
                      <button key={o.label} type="button" onClick={() => set({ anciennete_max_jours: value })}
                        data-testid={`campaign-anciennete-${o.days ?? "none"}`} aria-pressed={active}
                        className={cn("relative rounded-[11px] border px-[11px] py-2.5 text-left transition-all duration-150",
                          active
                            ? "border-[#005bd3] bg-[#e8f1fd] shadow-[0_0_0_3px_rgba(0,91,211,.14)]"
                            : "border-[#e3e3e3] bg-white shadow-[0_1px_2px_rgba(15,23,42,.04)] hover:border-[#d0d0d0]")}>
                        <div className={cn("text-[13px] font-semibold leading-tight", active ? "text-[#00427a]" : "text-[#0f172a]")}>{o.label}</div>
                        <div className={cn("mt-[3px] font-mono text-[11px]", active ? "text-[#3b82c4]" : "text-[#94a3b8]")}>{o.sub}</div>
                        {active && (
                          <span className="absolute right-2 top-2 flex h-4 w-4 items-center justify-center rounded-full bg-[#005bd3] text-white">
                            <Check size={11} />
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2.5 border-t border-dashed border-[#e3e3e3] pt-3">
            <span className="flex items-center gap-1.5 text-xs font-medium text-[#475569]">
              <SlidersHorizontal size={13} className="text-[#94a3b8]" /> Fenêtre personnalisée
            </span>
            <div className="flex items-center gap-1.5">
              <input value={onTile ? "" : days} onChange={(e) => onCustom(e.target.value)} inputMode="numeric" placeholder="90"
                data-testid="campaign-anciennete-custom" aria-label="Ancienneté maximale en jours"
                className="h-9 w-[74px] rounded-[9px] border border-[#d0d0d0] px-2.5 font-mono text-[13px] outline-none transition-all focus:border-[#005bd3] focus:ring-[3px] focus:ring-[rgba(0,91,211,.16)]" />
              <span className="text-[13px] text-[#64748b]">jours max.</span>
            </div>
          </div>
        </div>
        )}

        {!isGoogle && days && (
          /* L'API gouvernementale n'a aucun filtre de date : le tri se fait
             après réception. Sur une zone étroite le résultat est souvent
             nul — mieux vaut prévenir que laisser croire à un bug. */
          <div className="flex items-start gap-2 rounded-xl bg-[#fdf3e7] px-3 py-[11px] text-xs leading-[1.5] text-[#8a6116]" data-testid="anciennete-hint">
            <Info size={14} className="mt-0.5 shrink-0" />
            <span>Les entreprises récentes sont rares : sur une seule commune le résultat peut être vide. Élargissez à un département ou plusieurs codes postaux.</span>
          </div>
        )}
      </div>

      {/* Pied figé */}
      <div className="flex shrink-0 justify-end gap-2 border-t border-[#ebebeb] bg-[#fafafa] px-[22px] py-4">
        <button type="button" onClick={onClose}
          className="h-[42px] rounded-[11px] border border-[#e3e3e3] bg-white px-4 text-sm font-medium text-[#303030] transition-colors hover:bg-[#f4f5f7]">
          Annuler
        </button>
        <button type="button" data-testid="save-campaign" onClick={save} disabled={saving}
          className="h-[42px] rounded-[11px] bg-[#005bd3] px-[18px] text-sm font-semibold text-white shadow-[0_6px_14px_-8px_rgba(0,91,211,.9)] transition-colors hover:bg-[#00479f] disabled:opacity-60">
          {saving ? "Enregistrement…" : isEdit ? "Enregistrer" : "Créer la campagne"}
        </button>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Sources d'extraction (libellés de repli : le serveur en envoie la
// description et l'état de configuration via /campaigns/sources).
// ---------------------------------------------------------------------------
const SOURCE_TILES = [
  { key: "recherche-entreprises", label: "Registre officiel (SIRENE)", icon: Landmark,
    hint: "Toutes les sociétés immatriculées, filtrées par code NAF. Gratuit." },
  { key: "google_places", label: "Google Places", icon: Globe,
    hint: "Établissements indexés par Google : enseigne, téléphone, site, note. Facturé par Google." },
];

/** Coût maximal d'une campagne Google, en clair : N zones × pages max × 20 fiches. */
export function googleCostText(cpCount, departement, objectif, source, typeLabel, query) {
  const pages = source?.max_pages_per_target || 3;
  const size = source?.page_size || 20;
  const quoi = [typeLabel, query].filter(Boolean).join(" ") || "…";
  if (!cpCount && !departement) return `Recherche « ${quoi} » — indiquez des codes postaux ou un département pour estimer le nombre d'appels.`;
  if (!cpCount) return `Recherche « ${quoi} » sur chaque code postal du département ${departement} : jusqu'à ${pages} appels facturés par code postal (${size} fiches par appel), arrêt à ${objectif || "l'objectif"} prospects.`;
  const max = cpCount * pages;
  return `Recherche « ${quoi} » sur ${cpCount} ${cpCount > 1 ? "codes postaux" : "code postal"} : jusqu'à ${max} appel${max > 1 ? "s" : ""} facturé${max > 1 ? "s" : ""} par Google (${pages} pages de ${size} fiches par code postal), arrêt dès ${objectif || "l'objectif"} prospects.`;
}

// ---------------------------------------------------------------------------
// Codes postaux du département : le système propose, l'utilisateur choisit.
// ---------------------------------------------------------------------------
const DEPT_RE = /^(\d{2,3}|2[AB])$/i;
const parseCodes = (text) => (text || "").split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);

const CHIP = "flex min-w-0 items-center gap-1.5 rounded-[9px] border px-2 py-1.5 text-left text-[12px] transition-colors";

/**
 * Sous le champ « Codes postaux » : la liste des codes du département saisi,
 * avec ceux qu'une autre campagne a déjà analysés. Un clic ajoute ou retire
 * le code du champ texte, qui reste la seule source de vérité — on peut
 * toujours taper un code que la liste ignore.
 */
function PostalCodePicker({ departement, selected, onChange, excludeId }) {
  const dep = (departement || "").trim().toUpperCase();
  const valid = DEPT_RE.test(dep);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [onlyFree, setOnlyFree] = useState(false);

  // Léger délai : on attend la fin de la frappe avant d'interroger le serveur.
  useEffect(() => {
    if (!valid) { setData(null); return undefined; }
    let cancelled = false;
    setLoading(true); setFailed(false);
    const t = setTimeout(() => {
      api.get("/campaigns/postal-codes", { params: { departement: dep, exclude: excludeId || undefined } })
        .then(({ data: d }) => { if (!cancelled) setData(d); })
        .catch(() => { if (!cancelled) { setData(null); setFailed(true); } })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [dep, valid, excludeId]);

  const chosen = useMemo(() => new Set(selected), [selected]);
  const items = data?.items || [];
  const shown = onlyFree ? items.filter((i) => !i.analysed) : items;
  const free = items.filter((i) => !i.analysed).map((i) => i.code_postal);

  const toggle = (cp) => onChange(chosen.has(cp) ? selected.filter((c) => c !== cp) : [...selected, cp]);
  const addMany = (codes) => onChange([...selected, ...codes.filter((c) => !chosen.has(c))]);
  const removeListed = () => { const listed = new Set(items.map((i) => i.code_postal)); onChange(selected.filter((c) => !listed.has(c))); };

  if (!valid) {
    return (
      <p className="mt-1.5 text-[11.5px] text-[#94a3b8]">
        Renseignez le département : ses codes postaux vous seront proposés, avec ceux déjà analysés par d'autres campagnes.
      </p>
    );
  }
  if (loading) {
    return (
      <p className="mt-1.5 flex items-center gap-1.5 text-[11.5px] text-[#64748b]" data-testid="cp-picker-loading">
        <Loader2 size={12} className="animate-spin" /> Recherche des codes postaux du {dep}…
      </p>
    );
  }
  if (failed || !data) {
    return <p className="mt-1.5 text-[11.5px] text-[#94a3b8]">Codes postaux du {dep} indisponibles — saisissez-les à la main.</p>;
  }
  if (items.length === 0) {
    return <p className="mt-1.5 text-[11.5px] text-[#94a3b8]">Aucun code postal connu pour le département {dep}.</p>;
  }

  const selectedHere = items.filter((i) => chosen.has(i.code_postal)).length;
  return (
    <div className="mt-2 rounded-[14px] border border-[#ebebeb] bg-[#fcfcfd] p-3" data-testid="cp-picker">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[12px] text-[#475569]">
        <span className="flex items-center gap-1.5 font-semibold">
          <MapPin size={13} className="text-[#005bd3]" /> Département {data.departement} — {data.total} codes postaux
        </span>
        <span data-testid="cp-picker-summary">
          <b className="text-[#b45309]">{data.analysed}</b> déjà analysé{data.analysed > 1 ? "s" : ""} par d'autres campagnes
          {" · "}<b className="text-[#005bd3]">{selectedHere}</b> sélectionné{selectedHere > 1 ? "s" : ""}
        </span>
        {data.source === "crm" && (
          <span className="rounded-md bg-[#fdf3dd] px-1.5 py-0.5 text-[11px] text-[#8a6b1f]" title="Le référentiel national n'a pas répondu : seuls les codes déjà connus du CRM sont listés">
            liste partielle
          </span>
        )}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <button type="button" data-testid="cp-add-free" onClick={() => addMany(free)} disabled={free.length === 0}
          className="rounded-lg border border-[#005bd3] bg-[#e8f1fd] px-2.5 py-1 text-[12px] font-medium text-[#00479f] hover:bg-[#d8e8fb] disabled:opacity-40">
          Ajouter les {free.length} non analysés
        </button>
        <button type="button" data-testid="cp-add-all" onClick={() => addMany(items.map((i) => i.code_postal))}
          className="rounded-lg border border-[#d0d0d0] bg-white px-2.5 py-1 text-[12px] text-[#475569] hover:bg-[#fafafa]">
          Tout ajouter
        </button>
        <button type="button" data-testid="cp-remove-all" onClick={removeListed} disabled={selectedHere === 0}
          className="rounded-lg border border-[#d0d0d0] bg-white px-2.5 py-1 text-[12px] text-[#475569] hover:bg-[#fafafa] disabled:opacity-40">
          Tout retirer
        </button>
        <label className="ml-auto flex cursor-pointer items-center gap-1.5 text-[12px] text-[#475569]">
          <input type="checkbox" checked={onlyFree} onChange={(e) => setOnlyFree(e.target.checked)} data-testid="cp-only-free" />
          Masquer les analysés
        </label>
      </div>

      <div className="mt-2 grid max-h-[220px] grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-1.5 overflow-y-auto pr-1">
        {shown.map((it) => {
          const on = chosen.has(it.code_postal);
          const who = (it.campaigns || []).map((c) => `${c.name}${c.status === "draft" ? " (brouillon)" : ""}`).join(", ");
          const title = [it.communes?.join(", "), who && (it.analysed ? `Analysé par : ${who}` : `Prévu par : ${who}`),
                         it.companies_count ? `${it.companies_count} entreprise${it.companies_count > 1 ? "s" : ""} en base` : null]
            .filter(Boolean).join("\n");
          return (
            <button key={it.code_postal} type="button" onClick={() => toggle(it.code_postal)} aria-pressed={on}
              data-testid={`cp-${it.code_postal}`} data-analysed={it.analysed ? "true" : "false"} title={title}
              className={cn(CHIP, on ? "border-[#005bd3] bg-[#e8f1fd] text-[#00479f]"
                : it.analysed ? "border-[#f3e2b8] bg-[#fdf8ec] text-[#8a6b1f] hover:border-[#e2c98a]"
                : "border-[#e3e3e3] bg-white text-[#334155] hover:border-[#b9c8dd]")}>
              <span className={cn(MONO, "shrink-0 font-semibold")}>{it.code_postal}</span>
              <span className="min-w-0 flex-1 truncate text-[11px] opacity-80">{it.communes?.[0] || ""}</span>
              {it.analysed ? <span className="shrink-0 rounded bg-[#f3e2b8]/70 px-1 text-[9.5px] font-semibold uppercase tracking-wide">analysé</span>
                : it.planned ? <span className="shrink-0 rounded bg-[#e2e8f0] px-1 text-[9.5px] font-semibold uppercase tracking-wide text-[#64748b]">prévu</span> : null}
              {on && <Check size={12} className="shrink-0" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}
